import Sugar from 'sugar';
import { html, css, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import type { PropertyValues } from 'lit';

import { callLambda, getActionFromLambdaResults } from '../../../../../helpers/lambda.js';

import NSJobComponent from '../../../job.js';

import JobSchema from '../../../../../models/job-schema.js';
import Company from '../../../../../models/company.js';
import CommercialRelationship from '../../../../../models/commercial-relationship.js';

import { ShareableRelationship, loadShareableConnections } from '../../../../../helpers/shareable-connections.js';

import '../../../../../ns-elements/ns-connection-picker.js';
import '../../../../../ns-elements/ns-relationship-summary.js';
import type { RelationshipSummaryParty } from '../../../../../ns-elements/ns-relationship-summary.js';
import '../../../../../ns-elements/ns-job-layout.js';
import { nsFormFieldStyles, nsLayoutStyles } from '../../../../../ns-styles/index.js';
import '@awesome.me/webawesome/dist/components/button/button.js';
import '@awesome.me/webawesome/dist/components/textarea/textarea.js';
import { depthAskPhrase, partiesSeenBy } from '../../../../../ns-relationship/relationship-terms.js';
import definition from '@job-schemas/company/connect/approve.json';
import { jobSchema } from '../../../job-schema.js';

@customElement(NSCompanyConnectApprove.is)
export default class NSCompanyConnectApprove extends NSJobComponent {
  static get is() {
    return 'ns-company-connect-approve';
  }

  @state()
  private __baseCompany?: Company;

  @state()
  private __partyCompanyId?: string;

  @state()
  private __relationship?: CommercialRelationship;

  @state()
  private __partyType?: string;

  @state()
  private __summaryParties: RelationshipSummaryParty[] = [];

  // Undecided/INVITED -> show accept/decline. Once accepted (just now, or already accepted when
  // this job loaded — the deferred follow-up case, see 10-depth-at-connect-time.md) -> show the
  // tag picker if there's an outstanding depth requirement, otherwise complete immediately.
  @state()
  private __accepted = false;

  @state()
  private __connections: ShareableRelationship[] = [];

  @state()
  private __selected: ReadonlySet<string> = new Set();

  @state()
  private __declining = false;

  @state()
  private __declineReason = '';

  static get schema(): JobSchema {
    return jobSchema(definition, { outcomes: { accept: { type: 'hidden' }, reject: { type: 'hidden' } } });
  }

  static styles = [
    nsLayoutStyles,
    ...super.styles,
    nsFormFieldStyles,
    css`
      ns-relationship-summary {
        margin-top: var(--wa-space-m);
        border-radius: var(--ns-card-border-radius, 0.5em);
        background: var(--wa-color-surface-lowered);
      }
    `,
  ];

  async connectedCallback() {
    try {
      await super.connectedCallback();

      if (!this._db) throw new Error('Unable to find db service');

      if (!this._auth) throw new Error('Unable to find auth service');

      const relationshipId = this.job.inputValues.relationshipId;
      const partyCompanyId = this.job.companyId;
      if (!relationshipId || typeof relationshipId !== 'string') throw new Error('Missing required relationship ID');
      if (!partyCompanyId || typeof partyCompanyId !== 'string') throw new Error('Missing required company ID');
      this.__partyCompanyId = partyCompanyId;

      this.__relationship = await this._db.getById<CommercialRelationship>('comRelationship', relationshipId);
      if (!this.__relationship) throw new Error(`Unable to find relationship with id ${relationshipId}`);

      const myParty = this.__relationship.parties.find((p) => p.companyId === partyCompanyId);
      if (!myParty) throw new Error(`Company ${partyCompanyId} is not a party to this relationship`);
      this.__partyType = myParty.partyType;
      // A decline already recorded whose card wasn't closed: there is nothing left to answer
      if (myParty.status === 'DECLINED') {
        this.outcome('reject');
        return;
      }

      const clientParty = this.__relationship.parties.find((p) => p.partyType === 'CLIENT');
      if (!clientParty) throw new Error('Relationship is missing a CLIENT party');

      this.__baseCompany = await this._db.getById<Company>('company', clientParty.companyId);
      if (!this.__baseCompany) throw new Error(`Unable to find company with id ${clientParty.companyId}`);

      this.__summaryParties = await this.__loadSummaryParties(this.__relationship, partyCompanyId);

      // Already accepted when this job loaded means this is the deferred follow-up case
      // (10-depth-at-connect-time.md) — go straight to the tag step, no accept/decline to show.
      this.__accepted = (myParty.status ?? 'ACCEPTED') === 'ACCEPTED';
      if (this.__accepted && this.__needsTagging(myParty.networkAccessStatus)) {
        await this.__loadTagCandidates();
      }
    } catch (err: unknown) {
      this.failJob(err, 'Unexpected error occurred while loading companies');
    }
  }

  private async __loadSummaryParties(
    relationship: CommercialRelationship,
    partyCompanyId: string,
  ): Promise<RelationshipSummaryParty[]> {
    const parties = await Promise.all(
      partiesSeenBy(relationship.parties, partyCompanyId).map(async (party) => {
        const company = await this._db?.getById<Company>('company', party.companyId);
        const role = Sugar.String.capitalize(party.partyType, true, true);
        return company ? { company, role: party.companyId === partyCompanyId ? `${role} (you)` : role } : undefined;
      }),
    );
    return parties.filter((party): party is RelationshipSummaryParty => !!party);
  }

  // undefined/null/0 mean no depth was requested; a positive number means there is one. GRANTED
  // means fully satisfied already — anything else (PENDING, PARTIALLY_GRANTED, or no status
  // recorded yet) means there's still something to tag.
  private __needsTagging(networkAccessStatus: string | null | undefined): boolean {
    const requestedDepth = this.__relationship?.requestedDepth;
    const hasRequirement = !!requestedDepth;
    if (!hasRequirement) return false;
    return networkAccessStatus !== 'GRANTED';
  }

  private async __loadTagCandidates() {
    if (!this._db) throw new Error('Missing db service');
    if (!this.__partyCompanyId) throw new Error('Missing required party company');
    if (!this.__relationship?.id) throw new Error('Missing required relationship');

    const grantorId = this.__partyCompanyId;
    const requesterId = this.__baseCompany?.id;
    if (!requesterId) throw new Error('Missing required client company');

    this.__connections = await loadShareableConnections(this._db, grantorId, requesterId, 'DOWN');
  }

  private async __respond(decision: 'accept' | 'reject') {
    if (!this._auth) throw new Error('Missing auth service');
    if (!this.__partyCompanyId) throw new Error('Missing required party company');
    if (!this.__relationship?.id) throw new Error('Missing required relationship');

    try {
      this.loading(decision === 'accept' ? 'Connecting Organisations' : 'Declining Request');
      const lambdaResult = await callLambda(
        'company/connect-review',
        'POST',
        this._auth.token,
        JSON.stringify({
          relationshipId: this.__relationship.id,
          party: this.__partyCompanyId,
          decision,
          ...(decision === 'reject' && this.__declineReason.trim() ? { reason: this.__declineReason.trim() } : {}),
        }),
      );

      await getActionFromLambdaResults(lambdaResult, 'commercial_relationship_updated');
      this.loaded();

      if (decision === 'reject') {
        this.outcome('reject');
        return;
      }

      this.__accepted = true;
      if (this.__needsTagging(null)) {
        await this.__loadTagCandidates();
      } else {
        this.outcome('accept');
      }
    } catch (err) {
      this.loaded();
      this.failJob(err, 'Unexpected error occurred while responding to the request');
    }
  }

  private async __tag() {
    if (!this._auth) throw new Error('Missing auth service');
    if (!this.__partyCompanyId) throw new Error('Missing required party company');
    if (!this.__relationship?.id) throw new Error('Missing required relationship');

    if (this.__selected.size < 1) {
      this.fatalError('Choose at least one supplier to share, or skip for now.');
      return;
    }

    try {
      this.loading('Sharing suppliers');
      const lambdaResult = await callLambda(
        'company/connect-network-access-tag',
        'POST',
        this._auth.token,
        JSON.stringify({
          relationshipId: this.__relationship.id,
          party: this.__partyCompanyId,
          sharedRelationshipIds: [...this.__selected],
        }),
      );

      if (lambdaResult.res.code !== 200) throw new Error('Failed to share the selected suppliers');
      this.loaded();
      this.outcome('accept');
    } catch (err) {
      this.loaded();
      this.failJob(err, 'Unexpected error occurred while tagging connections');
    }
  }

  private __skipTagging() {
    this.outcome('accept');
  }

  private get __baseCompanyName() {
    return this.__baseCompany?.name ? Sugar.String.capitalize(this.__baseCompany.name, true, true) : '';
  }

  private get __depth() {
    return this.__relationship?.requestedDepth ?? 0;
  }

  // The header names the step; the schema's own title stays underneath
  protected willUpdate(changedProperties: PropertyValues) {
    super.willUpdate(changedProperties);
    this.jobTitle = this.__accepted ? 'Choose which suppliers to share' : undefined;
  }

  private __renderTagStep() {
    const baseCompany = this.__baseCompanyName;
    return html`
      <ns-job-layout>
        <p slot="intro">
          <strong>${this.__baseCompanyName}</strong>
          asked to see ${depthAskPhrase(this.__depth)}. Choose which of your suppliers they can see; nothing is shared
          until you do.
        </p>
        ${this.__connections.length > 0
          ? html`
              <ns-connection-picker
                class="job-panel"
                label="Your suppliers"
                select-all-label="Share all"
                share-with=${this.__baseCompany?.name ?? ''}
                .connections=${this.__connections}
                .selected=${this.__selected}
                @connections-change=${(e: CustomEvent<{ selected: Set<string> }>) =>
                  (this.__selected = e.detail.selected)}
              >
                <p class="small">
                  ${baseCompany} will see each supplier you share and who's on that relationship, but not its contract
                  value or notes,
                  ${this.__depth > 1
                    ? 'and further down only where those suppliers choose to share too.'
                    : 'and nothing past it.'}
                  Suppliers you add later aren't shared unless you choose them.
                </p>
                <p class="small">
                  If you skip, nothing is shared. You'll be asked again when you add a supplier of your own, or if
                  ${baseCompany} asks to see further.
                </p>
              </ns-connection-picker>
            `
          : html`
              <div class="job-panel">
                <p>You don't have any suppliers on nodeStream yet. You'll be asked again when you add one.</p>
              </div>
            `}
      </ns-job-layout>
    `;
  }

  private __renderRespondStep() {
    const baseCompany = this.__baseCompanyName;
    const partyType = this.__partyType ? Sugar.String.capitalize(this.__partyType, true, true) : '';
    const notes = this.job.inputValues.notes;

    return html`
      <ns-job-layout>
        <p slot="intro">
          <strong>${baseCompany}</strong>
          wants to connect with you as a ${partyType}. This creates a commercial relationship between you — the basis
          for sharing data, requesting deeper network access, and collaborating through shared workflows.
        </p>
        <div class="job-panel">
          <h3>Review before you decide</h3>
          <p>Read their note and what this connection means, then accept or decline below.</p>
          <p class="note">
            <strong>Their note:</strong>
            ${notes || 'No note given.'}
          </p>
        </div>
        ${this.__depth
          ? html`
              <div class="job-panel">
                <h3>They're also asking to see ${depthAskPhrase(this.__depth)}</h3>
                <p>
                  If you accept, you'll choose next which of your suppliers ${baseCompany} can see. Nothing is shared
                  until you choose, and you can skip it for now.
                </p>
              </div>
            `
          : nothing}
        ${this.__declining
          ? html`
              <div class="job-panel">
                <h3>Decline ${baseCompany}'s request?</h3>
                <p>${baseCompany} will be told you declined.</p>
                <wa-textarea
                  label="Reason (optional, shown only to ${baseCompany})"
                  maxlength="500"
                  .value=${this.__declineReason}
                  @input=${(e: Event) => (this.__declineReason = (e.target as HTMLTextAreaElement).value)}
                ></wa-textarea>
              </div>
            `
          : nothing}
        <ns-relationship-summary
          .parties=${this.__summaryParties}
          .contractValue=${this.__relationship?.contractValue}
          contractCurrency=${this.__relationship?.contractCurrency ?? 'GBP'}
          .reviewDate=${this.__relationship?.reviewDate}
          .requestedDepth=${this.__relationship?.requestedDepth}
          depthLabel=${this.__depth
            ? `${Sugar.String.capitalize(depthAskPhrase(this.__depth))}, if you share them`
            : 'Not asked'}
        ></ns-relationship-summary>
        <div slot="aside" class="primary-info">
          <span class="info-icon" aria-hidden="true">i</span>
          <h3>What this means</h3>
          <ul class="ticks">
            <li>
              As a
              <strong>${partyType}</strong>
              you'll be connected with ${baseCompany}.
            </li>
            <li>You'll be able to share data and workflows with ${baseCompany}.</li>
            ${this.__depth
              ? html`
                  <li>They'll see only the suppliers you choose to share.</li>
                `
              : nothing}
            <li>Either of you can end the relationship later.</li>
          </ul>
        </div>
      </ns-job-layout>
    `;
  }

  render() {
    return this.__accepted ? this.__renderTagStep() : this.__renderRespondStep();
  }

  renderActions() {
    if (this.__accepted) {
      return html`
        <wa-button variant="brand" appearance="outlined" type="button" @click=${() => this.__skipTagging()}>
          Skip for now
        </wa-button>
        ${this.__connections.length > 0
          ? html`
              <wa-button variant="brand" type="button" @click=${() => this.__tag()}>Share and finish</wa-button>
            `
          : nothing}
      `;
    }
    if (this.__declining) {
      return html`
        <wa-button variant="brand" appearance="outlined" type="button" @click=${() => (this.__declining = false)}>
          Keep it
        </wa-button>
        <wa-button variant="danger" type="button" @click=${() => this.__respond('reject')}>Decline request</wa-button>
      `;
    }
    return html`
      <wa-button variant="brand" appearance="outlined" type="button" @click=${() => (this.__declining = true)}>
        Decline
      </wa-button>
      <wa-button variant="brand" type="button" @click=${() => this.__respond('accept')}>Accept</wa-button>
    `;
  }
}
